import { useState, useMemo } from "react";
import { useTranslation } from "@/packages/utils";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Terminal, ChevronRight, Shield } from "lucide-react";
import { useAdminActions } from "./admin-action-registry";
import { AdminActionPanel } from "./AdminActionPanel";
import type { AdminActionConfig } from "./admin-action-registry";

export function AdminActionsPage() {
  const { t } = useTranslation(["admin"]);
  const groups = useAdminActions();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const allActions = useMemo(() => groups.flatMap((g) => g.actions), [groups]);
  const expandedConfig = allActions.find((a) => a.id === expandedId) ?? null;

  return (
    <div className="relative min-h-full">
      {/* Subtle grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.4) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      <div className="relative mx-auto max-w-3xl px-6 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 ring-1 ring-amber-500/20">
              <Terminal className="h-4.5 w-4.5 text-amber-600" />
            </div>
            <h1
              className="text-2xl font-bold tracking-tight text-foreground"
              style={{ fontFamily: "'JetBrains Mono', monospace" }}
            >
              {t("actions.title")}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground ml-12">
            {t("actions.pageDescription")}
          </p>
        </motion.div>

        {/* Grouped Action Cards */}
        <div className="space-y-8">
          {groups.map((group) => (
            <div key={group.group}>
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {group.group}
              </h2>
              <div className="space-y-3">
                {group.actions.map((action, index) => (
                  <ActionCard
                    key={action.id}
                    config={action}
                    index={index}
                    isExpanded={expandedId === action.id}
                    onToggle={() =>
                      setExpandedId((prev) =>
                        prev === action.id ? null : action.id,
                      )
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Empty state when no actions */}
        {allActions.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-24 text-center"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/50 ring-1 ring-border mb-4">
              <Shield className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              {t("actions.noActions")}
            </p>
          </motion.div>
        )}
      </div>

      {/* Expanded panel overlay */}
      <AnimatePresence>
        {expandedConfig &&
          (expandedConfig.customPanel ? (
            <expandedConfig.customPanel
              key={expandedConfig.id}
              onClose={() => setExpandedId(null)}
            />
          ) : (
            <AdminActionPanel
              key={expandedConfig.id}
              config={expandedConfig}
              onClose={() => setExpandedId(null)}
            />
          ))}
      </AnimatePresence>
    </div>
  );
}

function ActionCard({
  config,
  index,
  isExpanded,
  onToggle,
}: {
  config: AdminActionConfig;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation(["admin"]);
  const isDangerous = config.dangerous;

  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.45,
        delay: index * 0.08,
        ease: [0.22, 1, 0.36, 1],
      }}
      onClick={onToggle}
      className={`
        group relative w-full text-left rounded-xl
        border bg-card p-5 transition-all duration-200
        hover:shadow-md hover:border-border/80
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
        ${isDangerous ? "border-amber-200/60 hover:border-amber-300/80" : "border-border"}
        ${isExpanded ? "ring-2 ring-amber-400/30 border-amber-300" : ""}
      `}
    >
      {/* Danger accent stripe */}
      {isDangerous && (
        <div className="absolute left-0 top-3 bottom-3 w-1 rounded-full bg-amber-400/70" />
      )}

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3.5 min-w-0">
          <div
            className={`
              mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg
              ${isDangerous ? "bg-amber-500/10 ring-1 ring-amber-500/20" : "bg-muted ring-1 ring-border"}
            `}
          >
            {isDangerous ? (
              <AlertTriangle className="h-4.5 w-4.5 text-amber-600" />
            ) : (
              <Terminal className="h-4.5 w-4.5 text-muted-foreground" />
            )}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2.5 mb-1">
              <span
                className="text-sm font-semibold text-foreground truncate"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {config.label}
              </span>
              {isDangerous && (
                <span className="inline-flex items-center rounded-md bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-600 ring-1 ring-red-200/60">
                  {t("admin:standards.dialogs.destructiveLabel")}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
              {config.description}
            </p>
          </div>
        </div>

        <ChevronRight
          className={`
            mt-2.5 h-4 w-4 shrink-0 text-muted-foreground/50
            transition-transform duration-200
            group-hover:text-muted-foreground
            ${isExpanded ? "rotate-90" : ""}
          `}
        />
      </div>

      {/* Field preview chips */}
      <div className="mt-3.5 ml-[3.375rem] flex flex-wrap gap-1.5">
        {(config.fields ?? []).map((field) => (
          <span
            key={field.name}
            className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-0.5 text-[11px] text-muted-foreground ring-1 ring-border/50"
          >
            <span className="text-muted-foreground/50">→</span>
            {field.label}
          </span>
        ))}
      </div>
    </motion.button>
  );
}
